import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Copy, Mail, Banknote, ArrowLeft, Info } from 'lucide-react';
import { toast } from 'sonner';

const PreOrderEtransferPage = () => {
  const [searchParams] = useSearchParams();
  const childName = searchParams.get('child') || 'your child';
  const total = searchParams.get('total') || '0';
  const eventSlug = searchParams.get('event');
  const [copied, setCopied] = useState(false);

  const etransferEmail = 'liss_nick@hotmail.com';

  const copyEmail = () => {
    navigator.clipboard.writeText(etransferEmail);
    setCopied(true);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-8">
      <div className="max-w-xl mx-auto px-4">
        <Card className="border-green-200">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-800">Order Placed!</CardTitle>
            <CardDescription className="text-base">
              Complete your payment via Interac e-Transfer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Order for:</span>
                <span className="font-semibold">{childName}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-stone-600">Amount Due:</span>
                <span className="font-bold text-lg text-amber-600">{formatCurrency(parseFloat(total))}</span>
              </div>
            </div>

            {/* E-Transfer Instructions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                <Banknote className="h-5 w-5 text-amber-600" />
                E-Transfer Instructions
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                  <div>
                    <p className="font-medium">Send e-Transfer to:</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-slate-100 px-3 py-2 rounded text-sm font-mono flex-1">{etransferEmail}</code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyEmail}
                        className="flex-shrink-0"
                      >
                        {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                  <div>
                    <p className="font-medium">Amount:</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(parseFloat(total))}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                  <div>
                    <p className="font-medium">In the message/notes, include:</p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1">
                      <p className="font-semibold text-amber-800">"{childName}"</p>
                      <p className="text-sm text-amber-700 mt-1">(Your child's name)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auto-deposit Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Autodeposit Enabled</p>
                <p className="text-sm text-blue-700">
                  No security question needed - payment will be automatically deposited.
                </p>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-stone-700 mb-2">What happens next?</h4>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• Your order has been recorded in our system</li>
                <li>• Once we receive your e-Transfer, we'll mark your order as paid</li>
                <li>• You'll receive a confirmation email when payment is confirmed</li>
                <li>• After photos are taken, you'll receive a link to select your photos</li>
              </ul>
            </div>

            {/* Back to Pre-Order Page */}
            <div className="pt-4 border-t">
              <Link to={eventSlug ? `/preorder/${eventSlug}` : '/'}>
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {eventSlug ? 'Place Another Order' : 'Back to Home'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PreOrderEtransferPage;
